import React from 'react'
import TextInput from '../TextInput/TextInput'
import { MasterSkuInputGroupProps, ProductLike } from './types'

function MasterSkuInputGroup<T extends ProductLike = ProductLike>({
  value,
  linkedProduct,
  onLinkProduct,
  onUnlinkProduct,
  placeholder,
  className = ''
}: MasterSkuInputGroupProps<T>): JSX.Element {
  
  const defaultPlaceholder = linkedProduct 
    ? "Auto-generated from merchant product"
    : "Click Link button to generate SKU"

  return (
    <div className={`tw-w-full ${className}`}>
      <div className="tw-flex tw-gap-2 tw-items-center">
        <TextInput
          value={value}
          onChange={() => {}} // No-op since it's readonly
          readOnly={true}
          placeholder={placeholder || defaultPlaceholder}
          size="md"
          className="tw-flex-1 tw-min-w-0"
        />
        
        {/* Three-state button system */}
        {!linkedProduct ? (
          // State 1: Link button (when no product linked)
          <button 
            type="button"
            onClick={onLinkProduct}
            className="tw-px-3 tw-py-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-transition-colors tw-flex tw-items-center tw-gap-1.5 tw-shrink-0"
            title="Link to merchant product to generate Master SKU"
          >
            <span className="material-symbols-outlined tw-text-[16px]">link</span>
            <span className="tw-hidden sm:tw-inline">Link</span>
          </button>
        ) : (
          // State 2: Change + Remove buttons (when product linked)
          <>
            <button 
              type="button"
              onClick={onLinkProduct}
              className="tw-px-3 tw-py-2 tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-transition-colors tw-flex tw-items-center tw-gap-1.5 tw-shrink-0"
              title="Change linked merchant product"
            >
              <span className="material-symbols-outlined tw-text-[16px]">swap_horiz</span>
              <span className="tw-hidden sm:tw-inline">Change</span>
            </button>
            <button 
              type="button"
              onClick={onUnlinkProduct}
              className="tw-px-2 tw-py-2 tw-bg-red-600 hover:tw-bg-red-700 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-transition-colors tw-flex tw-items-center tw-shrink-0"
              title="Remove link and clear Master SKU"
            >
              <span className="material-symbols-outlined tw-text-[16px]">close</span>
            </button>
          </>
        )}
      </div>
      
      {/* Help text */}
      <p className="tw-text-xs tw-text-gray-500 dark:tw-text-gray-400 tw-mt-1">
        {linkedProduct 
          ? "This field is auto-generated based on the linked merchant product."
          : "Master SKU must be generated by linking to a merchant product. Click the Link button to select a merchant product."
        }
      </p>
      
      {/* Linked Merchant Product Display */}
      {linkedProduct && (
        <div className="tw-mt-2 tw-p-2 tw-bg-blue-50 dark:tw-bg-blue-900/20 tw-border tw-border-blue-200 dark:tw-border-blue-700 tw-rounded-md">
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="material-symbols-outlined tw-text-blue-600 dark:tw-text-blue-400 tw-text-[16px]">link</span>
            <span className="tw-text-xs tw-text-blue-700 dark:tw-text-blue-300 tw-font-medium">Linked to:</span>
            <span className="tw-text-xs tw-text-blue-700 dark:tw-text-blue-300">{linkedProduct.name}</span>
            {linkedProduct.store && (
              <span className="tw-text-xs tw-text-blue-600 dark:tw-text-blue-400">({linkedProduct.store})</span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default MasterSkuInputGroup